import React, { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isEmpty } from 'underscore';

import { AppContext, ContextType } from '@/shared/context';
import { properties } from '@/testData/companyProfile';
import { checkSupression } from '@/utils/index';
import { contactProfileLink } from '@/shared/constant';

const CompanyInfo = ({ props, countryCode }) => {
  const { isUnitedStates, isEuBd } = useContext(AppContext) as ContextType;
  const { t } = useTranslation();
  const hostUrl = window.location.href;
  const industryKey = isEuBd ? "euIndustries" : "industries";
  const [showMoreIndustries, toggleMoreIndustries] = useState(false);

  return (
    <div className="col-md-6 company-profile-overview-starting-margin">
      {props.tradeStyleName && (
        <div className="margin-between-rows">
          <span className="company_data_point company-profile-text" name="company_name">
            <label>{properties.business_name_label}:&nbsp;</label>
            <span
              data-tracking-name="{properties.business_name_label}"
              className="avenir-light"
            >
              {props.tradeStyleName}
            </span>
          </span>
        </div>
      )}

      {isUnitedStates && props.companyShortSummaryTest && (
        <div className="margin-between-rows">
          <span className="company_data_point company-profile-text" name="company_description">
            <label>{"Company Description"}:&nbsp;</label>
            <span
              data-tracking-name={"Company Description"}
              className="avenir-light"
            >
              {props.companyShortSummaryTest.replace(/[\t\s]/g, '') === '?' ?
                <button
                  type="button"
                  className="border-0"
                >
                  {checkSupression(props.companyShortSummaryTest)}
                </button> : <span>{props.companyShortSummaryTest}</span>}
            </span>
            {props.showReadMore && (
              <div className="d-print-none d-inline">
                <span className="overview-description-read-more">
                  ...
                  <br />
                  <a className="linkColorOverview">{"Read More"}</a>
                  <span className="chevron-down-arrow-blue"></span>
                </span>
                <span className="overview-description-read-less overview-view-hide">
                  <span>{props.descriptionReadMore}</span>
                  <br />
                  <a className="linkColorOverview">{"Read Less"}</a>
                  <span className="chevron-up-arrow-blue"></span>
                </span>
              </div>
            )}
          </span>
        </div>
      )}

      {isEuBd && (
        <div className="margin-between-rows">
          <span className="company_data_point company-profile-text d-flex" name="duns_number">
            <label>
              {t("companyProfile.overview.duns_nr_label")}:&nbsp;
            </label>
            <span
              data-tracking-name={t(
                "companyProfile.overview.duns_nr_label"
              )}
            >
              {hostUrl.includes('/de-de/') ? <a target="_blank" href={`/de-de/upik.html`}>
                <div className="dataMaskingLockIcon" />
              </a> : checkSupression(props.encryptedDuns)}

            </span>
          </span>
        </div>
      )}
      {props.registrationNumber && (
        <div className="margin-between-rows">
          <span className="company_data_point company-profile-text" name="reg_number">
            <label>
              {t("companyProfile.overview.registration_nr_label")}:&nbsp;
            </label>
            <span
              data-tracking-name={t(
                "companyProfile.overview.registration_nr_label"
              )}
              className="avenir-light"
            >
              {props.registrationNumber}
            </span>
          </span>
        </div>
      )}
      {countryCode === "pl" && (
        <>
          {props.regon && (
            <div className="margin-between-rows">
              <span className="company_data_point company-profile-text" name="regon">
                <label>Regon:&nbsp;</label>
                <span
                  data-tracking-name={properties.regon}
                  className="avenir-light"
                >
                  {props.regon}
                </span>
              </span>
            </div>
          )}
          {props.krs && (
            <div className="margin-between-rows">
              <span className="company_data_point company-profile-text" name="krs">
                <label>KRS:&nbsp;</label>
                <span
                  data-tracking-name={properties.krs}
                  className="avenir-light"
                >
                  {props.krs}
                </span>
              </span>
            </div>
          )}
        </>
      )}

      {props.ranks && (
        <div className="margin-between-rows">
          <span className="company_data_point company-profile-text" name="ranks">
            <label>{properties.ranking_label}:&nbsp;</label>
            {props.ranks.map((rank, index) => (
              <React.Fragment key={index}>
                <span className="avenir-light">
                  <span className="prefix avenir-light">
                    #{properties.rankPrefix}
                  </span>
                  {rank["value"]}&nbsp;
                </span>
                <span className="suffix">{properties.rankSuffix}</span>
                <span className="avenir-light">
                  {rank["description"]}
                </span>
              </React.Fragment>
            ))}
          </span>
        </div>
      )}

      {props.keyPrincipal && (
        <div className="margin-between-rows">
          <span className="company_data_point company-profile-text" name="key_principal">
            <label>
              {t("companyProfile.overview.key_principal_label")}:&nbsp;
            </label>
            <span>
              {props.keyPrincipalProfileLink ? (
                <span className="overview_arrow_link">
                  <a
                    href={`${contactProfileLink}${props.keyPrincipalProfileLink}`}
                    className="jump-link"
                  >
                    {props?.keyPrincipal}
                  </a>
                </span>
              ) : (
                <span className="avenir-light">{props.keyPrincipal}</span>
              )}{" "}
              <br />
              <span
                className="overview_arrow_link d-print-none"
              >
                <a
                  className="jump-link light-text"
                  href="#contact-list"
                >
                  {t("companyProfile.overview.seeMoreContactsLabel")}
                </a>
                <a
                  href="#contact-list"
                  className="chevron-right-arrow-blue cursor-pointer"
                ></a>
              </span>
            </span>
          </span>
        </div>
      )}

      {props[industryKey] && (
        <>
          <div className="margin-between-rows">
            <span className="company_data_point company-profile-text" name="industry_links">
              <label>
                {t("companyProfile.overview.industry_label")}:&nbsp;
              </label>
              {isEuBd && (
                <span>
                  <span className="avenir-light">
                    {props[industryKey]}
                  </span>
                </span>
              )}
              {isUnitedStates &&
                Object.entries(props[industryKey]).map(
                  ([label, href], index: number, array) => (
                    <div key={index} className="d-inline">
                      {href && (
                        <a
                          className="company_profile_overview_underline_links"
                          href={`/business-directory/industry-analysis.${href}.html`}
                        >
                          {label}
                        </a>
                      )}
                      {!href && <span>{label}</span>}
                      {index < array.length - 1 && ", "}
                    </div>
                  )
                )}
              {isUnitedStates && !isEmpty(props["viewMoreIndustries"]) && (
                <span>
                  <span className="overview-industries-view-more">
                    <br />
                    <a className="light-text">{"See All Industries"}</a>
                    <span className="chevron-down-arrow-blue"></span>
                  </span>
                  <span className="overview-view-more-industries-list overview-view-hide">
                    {Object.entries(props["viewMoreIndustries"]).map(
                      ([label, href], index, array) => (
                        <React.Fragment key={index}>
                          {href && (
                            <a
                              className="company_profile_overview_underline_links"
                              href={`/business-directory/industry-analysis.${href}.html`}
                            >
                              {label}
                            </a>
                          )}
                          {!href && <span>{label}</span>}
                          {index < array.length - 1 && ", "}
                        </React.Fragment>
                      )
                    )}
                  </span>
                  <span className="overview-industries-view-less overview-view-hide">
                    <br />
                    <a className="light-text">{"See Fewer Industries"}</a>
                    <span className="chevron-up-arrow-blue"></span>
                  </span>
                </span>
              )}
              {props.euViewMoreIndustries && isEuBd && (
                <>
                  {!showMoreIndustries ? (
                    <span className="overview-industries-view-more">
                      <br />
                      <a
                        onClick={() => {
                          toggleMoreIndustries(!showMoreIndustries);
                        }}
                        className="anchor light-text cursor-pointer"
                      >
                        {t(
                          "companyProfile.overview.industries_view_more_label"
                        )}
                      </a>
                      <span
                        onClick={() => {
                          toggleMoreIndustries(!showMoreIndustries);
                        }}
                        className="chevron-down-arrow-blue cursor-pointer"
                      ></span>
                    </span>
                  ) : (
                    <>
                      <span className="overview-view-more-industries-list avenir-light">
                        <span>
                          {props.euViewMoreIndustries}
                        </span>
                      </span>
                      <span className="overview-industries-view-less">
                        <br />
                        <a
                          onClick={() => {
                            toggleMoreIndustries(!showMoreIndustries);
                          }}
                          className="anchor light-text cursor-pointer"
                        >
                          {t(
                            "companyProfile.overview.industries_view_less_label"
                          )}
                        </a>
                        <span
                          onClick={() => {
                            toggleMoreIndustries(!showMoreIndustries);
                          }}
                          className="chevron-up-arrow-blue cursor-pointer"
                        ></span>
                      </span>
                    </>
                  )}
                </>
              )}
            </span>
          </div>{" "}
        </>
      )}

      {props.otherIndustries && (
        <div className="margin-between-rows">
          <span
            className="company_data_point company-profile-text"
            name="other_industries_links"
          >
            <label>
              {properties.other_industries_label}&nbsp;
              {props.parentIndustryName}&nbsp;
              {properties.other_industries_sector_label}:
            </label>
            {Object.entries(props["otherIndustries"]).map(
              ([label, href], index, array) => (
                <div key={index} className="d-inline">
                  {href && (
                    <a
                      className="company_profile_overview_underline_links"
                      href={`/business-directory/industry-analysis.${href}.html`}
                    >
                      {label}
                    </a>
                  )}
                  {!href && <span>{label}</span>}
                  {index < array.length - 1 && ", "}
                </div >
              )
            )}
            {props.viewMoreOtherIndustries && !isEmpty(props.viewMoreOtherIndustries) && (
              <span>
                <span className="overview-other-industries-view-more d-print-none">
                  <br />
                  <a className="linkColorOverview">{'View more'}</a>
                  <span className="chevron-down-arrow-blue"></span>
                </span>
                <span className="overview-view-more-other-industries-list overview-view-hide">
                  {Object.entries(props["viewMoreOtherIndustries"]).map(
                    ([label, href], index, array) => (
                      <React.Fragment key={index}>
                        {href && (
                          <a
                            className="company_profile_overview_underline_links linkColorOverview"
                            href={`/business-directory/industry-analysis.${href}.html`}
                          >
                            {label}
                          </a>
                        )}
                        {!href && <span>{label}</span>}
                        {index < array.length - 1 && ", "}
                      </React.Fragment>
                    )
                  )}
                </span>
                <span className="overview-other-industries-view-less overview-view-hide">
                  <br />
                  <a className="linkColorOverview">{'View fewer'}</a>
                  <span className="chevron-up-arrow-blue"></span>
                </span>
              </span>
            )}
          </span>
        </div>
      )}

      {props.popularIndustries && isUnitedStates && (
        <div className="margin-between-rows d-print-mt-25">
          <div className="company_data_point company-profile-text d-print-flex" name="popular_search_links">
            <label>{properties.popular_search_label}:&nbsp;</label>
            {props.popularIndustries.map((popularIndustry, indexs) => {
              if (popularIndustry.name) {
                return (
                  <div key={indexs}>
                    <span
                      className={`overview-popular-search overview-popular-color-theme-${popularIndustry.colorIndex}`}
                    >
                      <a href={`/business-directory/company-information.${popularIndustry.url}.html`}>
                        <span
                          className={`overview-popular-icon-${popularIndustry.colorIndex}`}
                        ></span>
                        <span>{popularIndustry.name}</span>
                      </a>
                    </span>
                  </div>
                );
              }
              return "";
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default CompanyInfo;